import { useEffect } from "react";
import React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  Sparkles,
  MapPin,
  KeyRound,
  DoorOpen,
  Car,
  Clock,
  Wifi,
  Wind,
  Music,
  Projector,
  Flame,
  Mail,
  MessageCircle,
  Instagram,
  Phone,
  UserCheck,
  Sun,
  Film,
  PlayCircle,
  Quote,
  Droplets,
  Flower2,
  Recycle,
  Timer,
  AlertTriangle,
  Info,
  Check,
  Shirt,
} from "lucide-react";
import { GUIDE_CONTENT } from "@/content/guide";
import { GUIDE_CONTENT_EN } from "@/content/guide.en";
import { GUIDE_UI } from "@/content/guideUI";
import { useContent } from "@/lib/ContentProvider";
import { useLanguage } from "@/lib/LanguageProvider";
import LanguageToggle from "@/components/LanguageToggle";

/**
 * GuideChapter — rendu d'un chapitre du Guide du Voyageur.
 *
 * Lit la structure du chapitre dans /content/guide.js et affiche
 * chaque bloc selon son type (callout, kv, list, steps, warning,
 * link, note). Hero photo optionnelle en tête, intro courte,
 * pied de page avec bouton "Revenir au sommaire".
 */

const ICONS = {
  MapPin, KeyRound, DoorOpen, Car, Clock, Wifi, Wind, Music, Projector,
  Flame, Mail, MessageCircle, Instagram, Sparkles, BookOpen, Phone, UserCheck,
  Sun, Film, PlayCircle, Droplets, Flower2, Recycle, Timer, Shirt,
  shirt: Shirt,
};

/**
 * renderRich — mini markdown-lite pour les valeurs de callouts.
 * Reconnaît uniquement `**texte**` → <strong>texte</strong> pour mettre
 * en valeur les durées clés (48 H, 12 H, etc.). Rien d'autre.
 */
function renderRich(text) {
  if (typeof text !== "string") return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong
          key={i}
          className="font-normal"
          style={{ color: "var(--cr-ruby)", fontVariantNumeric: "tabular-nums" }}
        >
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function GuideChapter() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { mediaUrl, content } = useContent();
  const { lang } = useLanguage();
  const uiT = (key) => GUIDE_UI[key]?.[lang] ?? GUIDE_UI[key]?.fr ?? "";
  const source = lang === "en" ? GUIDE_CONTENT_EN : GUIDE_CONTENT;
  // Fallback intelligent : si un chapitre n'est pas encore traduit en EN,
  // on retombe sur la version FR (aucune page vide).
  const chapter = source[slug] || GUIDE_CONTENT[slug];

  useEffect(() => {
    document.title = chapter
      ? `${chapter.title} — Guide · Curieux Refuge`
      : GUIDE_UI.docTitleUnknown?.[lang] ?? GUIDE_UI.docTitleUnknown?.fr ?? "";
  }, [chapter, lang]);

  // Chapitre inconnu → placeholder discret
  if (!chapter) {
    return <UnknownChapter navigate={navigate} uiT={uiT} />;
  }

  const { title, eyebrow, hero, intro, blocks } = chapter;
  const heroUrl = hero ? (mediaUrl ? mediaUrl(hero) : hero) : null;

  return (
    <div
      data-testid="guide-chapter-page"
      className="min-h-screen relative"
      style={{
        background:
          "linear-gradient(180deg, #FBF5EA 0%, #FDF9F0 40%, #FFFFFF 100%)",
      }}
    >
      {/* Papier — grain très subtil */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(46,31,24,0.35) 0px, rgba(46,31,24,0) 1px, rgba(46,31,24,0) 3px)",
        }}
      />

      {/* Sélecteur de langue — discret, top right */}
      <LanguageToggle />

      {/* Barre supérieure */}
      <div className="relative w-full max-w-2xl mx-auto px-5 sm:px-8 pt-6 md:pt-8 flex items-center">
        <button
          type="button"
          onClick={() => navigate("/guide")}
          data-testid="guide-chapter-back"
          className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.2em] uppercase transition-colors"
          style={{ color: "var(--cr-ink-soft)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--cr-ruby)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--cr-ink-soft)")}
        >
          <ArrowLeft size={14} strokeWidth={1.4} />
          {uiT("backToGuide")}
        </button>
      </div>

      {/* Hero photo optionnelle */}
      {heroUrl && (
        <div
          data-testid="guide-chapter-hero"
          className="cr-guide-open relative w-full max-w-2xl mx-auto mt-6 md:mt-8 px-5 sm:px-8"
        >
          <div
            className="w-full overflow-hidden"
            style={{
              borderRadius: "10px",
              border: "1px solid rgba(184, 153, 104, 0.35)",
              aspectRatio: "16 / 9",
            }}
          >
            <img
              src={heroUrl}
              alt={title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      )}

      {/* En-tête */}
      <header
        data-testid="guide-chapter-header"
        className="cr-guide-open relative w-full max-w-2xl mx-auto text-center px-5 sm:px-8 pt-8 md:pt-10 pb-6 md:pb-8"
      >
        <p
          className="font-body text-[10px] tracking-[0.32em] uppercase mb-3"
          style={{ color: "var(--cr-ruby)" }}
        >
          {eyebrow}
        </p>
        <h1
          className="font-display font-light leading-[1.05] tracking-tight"
          style={{
            color: "var(--cr-ink)",
            fontSize: "clamp(1.8rem, 4.4vw, 2.6rem)",
          }}
        >
          {title}
        </h1>
        {intro && (
          <p
            className="font-display italic font-light mx-auto mt-4"
            style={{
              color: "var(--cr-ink-soft)",
              fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)",
              lineHeight: 1.55,
              maxWidth: "34rem",
            }}
          >
            {intro}
          </p>
        )}
        <div className="flex items-center justify-center mt-6">
          <span
            className="h-[1px] w-14"
            style={{ background: "var(--cr-gold)", opacity: 0.75 }}
          />
        </div>
      </header>

      {/* Corps du chapitre */}
      <main
        data-testid="guide-chapter-body"
        className="relative w-full max-w-2xl mx-auto px-5 sm:px-8 pb-16 md:pb-24 space-y-10 md:space-y-12"
      >
        {blocks.map((block, i) => (
          <Block key={i} block={block} mediaUrl={mediaUrl} />
        ))}

        {/* Blocs ajoutés depuis l'admin (photos, encarts, mini-galeries) */}
        {(() => {
          const extras = content?.guideExtras?.[slug];
          if (!Array.isArray(extras) || extras.length === 0) return null;
          return (
            <div
              data-testid="guide-chapter-extras"
              className="space-y-10 md:space-y-12 pt-2"
            >
              {extras.map((block, i) => (
                <Block key={`extra-${i}`} block={block} mediaUrl={mediaUrl} />
              ))}
            </div>
          );
        })()}

        {/* Bouton retour au sommaire */}
        <div className="pt-6 flex justify-center">
          <Link
            to="/guide"
            data-testid="guide-chapter-back-to-sommaire"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-body text-[11px] tracking-[0.22em] uppercase transition-all duration-500"
            style={{
              background: "var(--cr-ruby)",
              color: "#FAF4EC",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--cr-ruby-deep)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--cr-ruby)")}
          >
            <BookOpen size={14} strokeWidth={1.4} />
            {uiT("backToSummary")}
          </Link>
        </div>
      </main>
    </div>
  );
}

// ============================================================
// Rendu des blocs — un composant par type
// ============================================================

function Block({ block, mediaUrl }) {
  switch (block.type) {
    case "hero-text":
      return <HeroText {...block} />;
    case "hero-facts":
      return <HeroFacts {...block} />;
    case "photo-caption":
      return <PhotoCaption {...block} mediaUrl={mediaUrl} />;
    case "pull-quote":
      return <PullQuote {...block} />;
    case "stats-row":
      return <StatsRow {...block} />;
    case "photo-text":
      return <PhotoText {...block} mediaUrl={mediaUrl} />;
    case "visual-pair":
      return <VisualPair {...block} mediaUrl={mediaUrl} />;
    case "compare-two":
      return <CompareTwo {...block} />;
    case "mode-tabs":
      return <ModeTabs {...block} mediaUrl={mediaUrl} />;
    case "precautions":
      return <Precautions {...block} />;
    case "section-title":
      return <SectionTitle {...block} />;
    case "paragraph":
      return <Paragraph {...block} />;
    case "whatsapp-cta":
      return <WhatsappCta {...block} />;
    case "video-coming":
      return <VideoComing {...block} />;
    case "callout":
      return <Callout {...block} mediaUrl={mediaUrl} />;
    case "kv":
      return <KV {...block} />;
    case "list":
      return <List {...block} />;
    case "steps":
      return <Steps {...block} />;
    case "warning":
      return <Warning {...block} />;
    case "note":
      return <Note {...block} />;
    case "link":
      return <ExternalLink {...block} />;
    case "tel":
      return <TelLink {...block} />;
    case "mail":
      return <MailLink {...block} />;
    case "insta":
      return <InstaLink {...block} />;
    case "gallery":
      return <Gallery {...block} mediaUrl={mediaUrl} />;
    default:
      return null;
  }
}

/** HeroText — grand display centré (adresse, message central) */
function HeroText({ eyebrow, value, subValue }) {
  return (
    <div className="text-center py-4 md:py-6">
      {eyebrow && (
        <p
          className="font-body text-[10px] tracking-[0.32em] uppercase mb-4"
          style={{ color: "var(--cr-ruby)" }}
        >
          {eyebrow}
        </p>
      )}
      <p
        className="font-display font-light whitespace-pre-line mx-auto"
        style={{
          color: "var(--cr-ink)",
          fontSize: "clamp(1.5rem, 3.8vw, 2.1rem)",
          lineHeight: 1.25,
          letterSpacing: "-0.005em",
          maxWidth: "28rem",
        }}
      >
        {value}
      </p>
      {subValue && (
        <p
          className="font-display italic font-light mt-3"
          style={{ color: "var(--cr-ink-soft)", fontSize: "1rem" }}
        >
          {subValue}
        </p>
      )}
      {/* Filet doré subtil */}
      <div className="flex items-center justify-center mt-5">
        <span
          className="h-[1px] w-14"
          style={{ background: "var(--cr-gold)", opacity: 0.7 }}
        />
      </div>
    </div>
  );
}

/**
 * HeroFacts — deux faits importants côte à côte (ex: "Arrivée autonome"
 * + "À partir de 14 h"). Chaque fait a une petite icône + label + valeur.
 */
function HeroFacts({ facts }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
      {facts.map((f, i) => {
        const Icon = ICONS[f.icon] || Sparkles;
        return (
          <div
            key={i}
            className="text-center py-6 px-4"
            style={{
              borderRadius: "10px",
              border: "1px solid rgba(184, 153, 104, 0.35)",
              background:
                "linear-gradient(180deg, rgba(255,253,249,0.85) 0%, rgba(255,253,249,0.4) 100%)",
            }}
          >
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3"
              style={{
                border: "1px solid rgba(139, 37, 48, 0.28)",
                background: "rgba(232, 184, 189, 0.14)",
              }}
            >
              <Icon size={17} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
            </span>
            <p
              className="font-body text-[10px] tracking-[0.24em] uppercase mb-1"
              style={{ color: "var(--cr-ruby)" }}
            >
              {f.label}
            </p>
            <p
              className="font-display font-light"
              style={{
                color: "var(--cr-ink)",
                fontSize: "clamp(1.1rem, 2.2vw, 1.4rem)",
                lineHeight: 1.2,
              }}
            >
              {f.value}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * PhotoCaption — photo (URL ou SVG placeholder) + caption structurée.
 * Utile pour "Boîte à clés" par exemple.
 */
function PhotoCaption({ src, alt, caption, subCaption, placeholder }) {
  const { mediaUrl } = useContent();
  const resolvedSrc = src ? (mediaUrl ? mediaUrl(src) : src) : null;

  return (
    <div className="mx-auto max-w-sm text-center">
      <div
        className="w-full overflow-hidden mx-auto"
        style={{
          borderRadius: "10px",
          border: "1px solid rgba(184, 153, 104, 0.45)",
          aspectRatio: "4 / 3",
          background: "rgba(255,253,249,0.6)",
        }}
      >
        {resolvedSrc ? (
          <img
            src={resolvedSrc}
            alt={alt || caption}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <KeyboxPlaceholder />
        )}
      </div>
      <p
        className="font-display font-light mt-4"
        style={{ color: "var(--cr-ink)", fontSize: "1.05rem" }}
      >
        {caption}
      </p>
      {subCaption && (
        <p
          className="font-body text-[11px] tracking-[0.2em] uppercase mt-1"
          style={{ color: "var(--cr-ruby)" }}
        >
          {subCaption}
        </p>
      )}
      {placeholder && !resolvedSrc && (
        <p
          className="font-display italic font-light mt-2"
          style={{ color: "var(--cr-ink-soft)", fontSize: "0.8rem", opacity: 0.7 }}
        >
          Photo à venir
        </p>
      )}
    </div>
  );
}

/** SVG placeholder — boîte à clés stylisée */
function KeyboxPlaceholder() {
  return (
    <svg
      viewBox="0 0 200 150"
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <rect x="0" y="0" width="200" height="150" fill="#FBF5EA" />
      {/* mur en pierre suggéré */}
      <rect x="0" y="0" width="200" height="150" fill="rgba(184,153,104,0.08)" />
      {/* boîte à clés */}
      <rect
        x="70" y="35" width="60" height="80"
        rx="4"
        fill="#F2E8D9"
        stroke="rgba(139, 37, 48, 0.35)"
        strokeWidth="1.2"
      />
      {/* écran chiffres */}
      <rect
        x="78" y="45" width="44" height="16"
        rx="1.5"
        fill="#2E1F18"
        stroke="rgba(184,153,104,0.6)"
      />
      <text
        x="100" y="57" textAnchor="middle"
        fontFamily="monospace" fontSize="10"
        fill="#B89968"
      >— — — —</text>
      {/* clavier 3x4 */}
      {[0,1,2].map(row => [0,1,2].map(col => (
        <circle
          key={`${row}-${col}`}
          cx={82 + col*13} cy={72 + row*11}
          r="3.5"
          fill="none"
          stroke="rgba(46,31,24,0.5)"
          strokeWidth="0.8"
        />
      )))}
      {/* poignée */}
      <rect x="90" y="105" width="20" height="4" rx="1" fill="rgba(46,31,24,0.4)" />
    </svg>
  );
}

/**
 * PullQuote — citation mise en évidence : gros italique centré,
 * guillemets dorés stylisés, filet gold. Utile pour les phrases fortes.
 */
function PullQuote({ value, footnote }) {
  return (
    <blockquote
      className="relative text-center px-6 py-8 md:py-10"
      style={{
        borderRadius: "10px",
        background:
          "linear-gradient(180deg, rgba(232,184,189,0.10) 0%, rgba(232,184,189,0.04) 100%)",
        border: "1px solid rgba(184, 153, 104, 0.30)",
      }}
    >
      <Quote
        size={22}
        strokeWidth={1.4}
        className="mx-auto mb-3"
        style={{ color: "var(--cr-gold)", opacity: 0.75 }}
      />
      <p
        className="font-display italic font-light mx-auto"
        style={{
          color: "var(--cr-ink)",
          fontSize: "clamp(1.15rem, 2.4vw, 1.4rem)",
          lineHeight: 1.4,
          maxWidth: "32rem",
        }}
      >
        {value}
      </p>
      {footnote && (
        <p
          className="font-body text-[10px] tracking-[0.24em] uppercase mt-4"
          style={{ color: "var(--cr-ruby)" }}
        >
          {footnote}
        </p>
      )}
      <div className="flex items-center justify-center mt-5">
        <span
          className="h-[1px] w-10"
          style={{ background: "var(--cr-gold)", opacity: 0.7 }}
        />
      </div>
    </blockquote>
  );
}

/**
 * StatsRow — rangée de 3 (ou 4) stats visuelles compactes :
 * grosse icône + valeur en gros + label. Idéal pour communiquer
 * plusieurs chiffres/faits d'un coup d'œil, sans encart textuel.
 */
function StatsRow({ items }) {
  return (
    <div
      className="grid gap-2 sm:gap-4"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((it, i) => {
        const Icon = ICONS[it.icon] || Sparkles;
        return (
          <div key={i} className="text-center">
            <span
              className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-2"
              style={{
                border: "1px solid rgba(139, 37, 48, 0.20)",
                background: "rgba(232, 184, 189, 0.14)",
              }}
            >
              <Icon size={18} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
            </span>
            <p
              className="font-display font-light leading-tight"
              style={{
                color: "var(--cr-ink)",
                fontSize: "clamp(1.05rem, 2vw, 1.25rem)",
              }}
            >
              {it.value}
            </p>
            <p
              className="font-body text-[9px] tracking-[0.2em] uppercase mt-1"
              style={{ color: "var(--cr-ruby)" }}
            >
              {it.label}
            </p>
            {it.subtext && (
              <p
                className="font-display italic font-light mt-1"
                style={{ color: "var(--cr-ink-soft)", fontSize: "0.72rem", opacity: 0.8 }}
              >
                {it.subtext}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * PhotoText — photo + bloc texte côte à côte (empilé sur mobile).
 * Reverse=true pour inverser l'ordre. Le bloc texte contient un titre
 * court, une phrase, et éventuellement un CTA (WhatsApp) ou vidéo.
 */
function PhotoText({ photo, alt, eyebrow, title, text, cta, video, reverse }) {
  const { mediaUrl } = useContent();
  const src = photo ? (mediaUrl ? mediaUrl(photo) : photo) : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-center">
      {src && (
        <div
          className="w-full overflow-hidden"
          style={{
            borderRadius: "10px",
            border: "1px solid rgba(184, 153, 104, 0.35)",
            aspectRatio: "4 / 3",
            order: reverse ? 2 : 0,
          }}
        >
          <img
            src={src}
            alt={alt || title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="space-y-3" style={{ order: reverse ? 1 : 0 }}>
        {eyebrow && (
          <p
            className="font-body text-[10px] tracking-[0.28em] uppercase"
            style={{ color: "var(--cr-ruby)" }}
          >
            {eyebrow}
          </p>
        )}
        {title && (
          <h3
            className="font-display font-light leading-tight"
            style={{
              color: "var(--cr-ink)",
              fontSize: "clamp(1.25rem, 2.2vw, 1.5rem)",
            }}
          >
            {title}
          </h3>
        )}
        {text && (
          <p
            className="font-display font-light"
            style={{
              color: "var(--cr-ink-soft)",
              fontSize: "0.95rem",
              lineHeight: 1.55,
            }}
          >
            {text}
          </p>
        )}
        {cta && (
          <div className="pt-1 flex md:justify-start justify-center">
            <WhatsappCta label={cta.label} message={cta.message} />
          </div>
        )}
        {video && (
          <div className="pt-1">
            <VideoComing label={video} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * VisualPair — pairage numéroté "équipement (1) → interrupteur (2)".
 * Deux miniatures côte à côte avec pastilles rouges numérotées en
 * haut à gauche de chaque photo. Optionnellement une note courte
 * en dessous. Idéal pour Éclairage & Son (lampadaire+interrupteur,
 * guirlande+télécommande, etc.).
 *
 * Si une seule photo est fournie, la pastille est masquée.
 * Si une photo est absente, un petit placeholder poétique s'affiche.
 */
function VisualPair({ eyebrow, photos = [], note }) {
  const { mediaUrl } = useContent();
  const showNumbers = photos.filter(Boolean).length > 1;
  const isSingle = photos.length === 1;

  return (
    <section>
      {eyebrow && (
        <h3
          className="font-display font-light italic mb-4"
          style={{ color: "var(--cr-ink)", fontSize: "1.05rem" }}
        >
          {eyebrow}
        </h3>
      )}

      <div
        className={isSingle ? "mx-auto max-w-sm" : "grid grid-cols-2 gap-4 md:gap-5"}
      >
        {photos.map((p, i) => {
          const src = p ? (mediaUrl ? mediaUrl(p) : p) : null;
          return (
            <div
              key={i}
              className="relative w-full overflow-hidden"
              style={{
                borderRadius: "6px",
                aspectRatio: "4 / 3",
                background: "rgba(255,253,249,0.6)",
              }}
            >
              {/* Pastille numérotée */}
              {showNumbers && (
                <span
                  aria-hidden="true"
                  className="absolute top-2 left-2 z-10 flex items-center justify-center w-6 h-6 rounded-full font-body font-medium text-[11px]"
                  style={{
                    background: "var(--cr-ruby)",
                    color: "#FAF4EC",
                    boxShadow: "0 2px 6px rgba(46, 31, 24, 0.25)",
                  }}
                >
                  {i + 1}
                </span>
              )}

              {src ? (
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex flex-col items-center justify-center gap-1 px-3 text-center"
                  style={{ border: "1px dashed rgba(184,153,104,0.35)", borderRadius: "6px" }}
                >
                  <Sparkles
                    size={16}
                    strokeWidth={1.2}
                    style={{ color: "var(--cr-ruby)", opacity: 0.55 }}
                  />
                  <p
                    className="font-display italic font-light"
                    style={{ color: "var(--cr-ink-soft)", fontSize: "0.72rem" }}
                  >
                    Photo à venir
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {note && (
        <p
          className="font-display italic font-light mt-3 whitespace-pre-line"
          style={{ color: "var(--cr-ink-soft)", fontSize: "0.9rem", lineHeight: 1.55 }}
        >
          {note}
        </p>
      )}
    </section>
  );
}

/**
 * SectionTitle — titre de section calligraphié avec filet doré.
 * Utile pour rythmer un chapitre long (Avant / Pendant / Après).
 */
function SectionTitle({ eyebrow, title, id }) {
  return (
    <div id={id} className="pt-4 md:pt-6 text-center scroll-mt-24">
      {eyebrow && (
        <p
          className="font-body text-[10px] tracking-[0.32em] uppercase mb-3"
          style={{ color: "var(--cr-ruby)" }}
        >
          {eyebrow}
        </p>
      )}
      <h2
        className="font-display font-light leading-tight"
        style={{ color: "var(--cr-ink)", fontSize: "clamp(1.5rem, 2.8vw, 1.9rem)" }}
      >
        {title}
      </h2>
      <div className="flex items-center justify-center mt-4">
        <span
          className="h-[1px] w-12"
          style={{ background: "var(--cr-gold)", opacity: 0.7 }}
        />
      </div>
    </div>
  );
}

/**
 * ModeTabs — sélecteur segmenté élégant à 2 (ou N) onglets.
 * L'onglet actif est piloté par le paramètre URL `?mode=<tabId>`.
 * Chaque `tab` peut contenir sa propre liste de blocs enfants.
 * Utilisé pour la section « Fonctionnement » du bassin (eau claire / bain de fleurs).
 */
function ModeTabs({ id, param = "mode", defaultId, tabs = [], mediaUrl }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRaw = searchParams.get(param);
  const activeId = tabs.some((t) => t.id === currentRaw)
    ? currentRaw
    : (defaultId || tabs[0]?.id);
  const activeTab = tabs.find((t) => t.id === activeId) || tabs[0];

  const setActive = (tabId) => {
    const next = new URLSearchParams(searchParams);
    next.set(param, tabId);
    setSearchParams(next, { replace: true });
    // Petit délai pour laisser React re-render puis scroller sur l'ancre pour rester dans la zone
    if (id) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 30);
    }
  };

  return (
    <section id={id} data-testid={`mode-tabs-${id || "default"}`} className="scroll-mt-24">
      {/* Segmented control */}
      <div
        role="tablist"
        aria-label="Choisir un mode"
        className="mx-auto max-w-md flex items-center gap-1 p-1 rounded-full mb-8"
        style={{
          background: "rgba(232, 184, 189, 0.10)",
          border: "1px solid rgba(184, 153, 104, 0.30)",
        }}
      >
        {tabs.map((t) => {
          const isActive = t.id === activeId;
          const Icon = ICONS[t.icon] || Sparkles;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(t.id)}
              data-testid={`mode-tab-${t.id}`}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full font-body text-[11px] tracking-[0.18em] uppercase transition-all duration-500"
              style={{
                background: isActive ? "var(--cr-ruby)" : "transparent",
                color: isActive ? "#FAF4EC" : "var(--cr-ink-soft)",
                boxShadow: isActive ? "0 4px 14px -6px rgba(139,37,48,0.45)" : "none",
              }}
            >
              <Icon
                size={13}
                strokeWidth={1.5}
                style={{ color: isActive ? "#FAF4EC" : "var(--cr-ruby)" }}
              />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
      {/* Contenu du tab actif */}
      {activeTab && (
        <div
          key={activeTab.id}
          role="tabpanel"
          className="space-y-10 md:space-y-12"
          style={{ opacity: 1, transition: "opacity 320ms ease" }}
        >
          {(activeTab.blocks || []).map((block, i) =>
            React.createElement(Block, {
              key: `${activeTab.id}-${i}`,
              block,
              mediaUrl,
            })
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Precautions — encadré doux et rassurant (non alarmant).
 * Utilisé pour les rappels sanitaires bain de fleurs / expériences sensorielles.
 */
function Precautions({ eyebrow, title, intro, avoid, tips, footer }) {
  return (
    <section
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(232,184,189,0.08) 0%, rgba(184,153,104,0.06) 100%)",
        border: "1px solid rgba(184, 153, 104, 0.30)",
        padding: "22px 22px 20px",
      }}
      data-testid="guide-precautions"
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
          style={{
            background: "rgba(232, 184, 189, 0.28)",
            border: "1px solid rgba(139, 37, 48, 0.22)",
          }}
        >
          <Info size={15} strokeWidth={1.5} style={{ color: "var(--cr-ruby)" }} />
        </span>
        <div className="flex-1">
          {eyebrow && (
            <p
              className="font-body text-[10px] tracking-[0.30em] uppercase mb-1.5"
              style={{ color: "var(--cr-ruby)" }}
            >
              {eyebrow}
            </p>
          )}
          {title && (
            <h3
              className="font-display font-light leading-tight"
              style={{ color: "var(--cr-ink)", fontSize: "1.15rem" }}
            >
              {title}
            </h3>
          )}
        </div>
      </div>
      {intro && (
        <p
          className="font-display font-light mb-4"
          style={{ color: "var(--cr-ink)", fontSize: "0.98rem", lineHeight: 1.6 }}
        >
          {intro}
        </p>
      )}
      {Array.isArray(avoid) && avoid.length > 0 && (
        <>
          <p
            className="font-body text-[10px] tracking-[0.24em] uppercase mb-2"
            style={{ color: "var(--cr-ink-soft)" }}
          >
            À lire avant de sauter dans le bain
          </p>
          <ul className="space-y-2 mb-4">
            {avoid.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 font-display font-light"
                style={{ color: "var(--cr-ink)", fontSize: "0.95rem", lineHeight: 1.55 }}
              >
                <span
                  className="mt-[10px] w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "var(--cr-ruby)", opacity: 0.55 }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {Array.isArray(tips) && tips.length > 0 && (
        <>
          <p
            className="font-body text-[10px] tracking-[0.24em] uppercase mb-2"
            style={{ color: "var(--cr-ink-soft)" }}
          >
            Nous vous conseillons également
          </p>
          <ul className="space-y-2">
            {tips.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 font-display font-light"
                style={{ color: "var(--cr-ink)", fontSize: "0.95rem", lineHeight: 1.55 }}
              >
                <Check
                  size={13}
                  strokeWidth={1.6}
                  className="mt-[6px] shrink-0"
                  style={{ color: "var(--cr-gold)" }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {footer && (
        <p
          className="font-display font-light italic mt-4"
          style={{ color: "var(--cr-ink-soft)", fontSize: "0.9rem", lineHeight: 1.55 }}
        >
          {footer}
        </p>
      )}
    </section>
  );
}

/** Paragraph — paragraphe de texte simple, aéré, lisible. */
function Paragraph({ text }) {
  return (
    <p
      className="font-display font-light whitespace-pre-line"
      style={{
        color: "var(--cr-ink)",
        fontSize: "clamp(0.98rem, 1.4vw, 1.02rem)",
        lineHeight: 1.6,
      }}
    >
      {text}
    </p>
  );
}

/**
 * WhatsappCta — CTA vert WhatsApp, en pill compact, avec icône
 * chat. Le href pointe directement vers wa.me/... avec un message
 * pré-rempli optionnel.
 */
function WhatsappCta({ label, message, sms, smsLabel }) {
  const { contact } = useContent();
  if (!contact?.whatsappNumber) return null;
  const url = message
    ? `https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(message)}`
    : `https://wa.me/${contact.whatsappNumber}`;
  // SMS optionnel : ouvre l'app Messages avec le corps pré-rempli
  const smsUrl = sms
    ? `sms:+${contact.whatsappNumber}?&body=${encodeURIComponent(message || "")}`
    : null;
  return (
    <div className="flex flex-wrap justify-center gap-3">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="guide-whatsapp-cta"
        className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all duration-500"
        style={{
          background: "#128C7E",
          color: "#FFFFFF",
          boxShadow: "0 4px 14px rgba(18, 140, 126, 0.25)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#0F7A6E";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#128C7E";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <MessageCircle size={15} strokeWidth={1.8} />
        {label}
      </a>
      {smsUrl && (
        <a
          href={smsUrl}
          data-testid="guide-sms-cta"
          className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all duration-500"
          style={{
            background: "var(--cr-ruby, #8B2530)",
            color: "#FFFFFF",
            boxShadow: "0 4px 14px rgba(139, 37, 48, 0.25)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <MessageCircle size={15} strokeWidth={1.8} />
          {smsLabel || "Par SMS"}
        </a>
      )}
    </div>
  );
}

/**
 * VideoComing — placeholder vidéo minimaliste : simple bouton
 * play grisé + libellé + petit mot "en préparation" en italique.
 * Aucun encart ni bordure — juste une note poétique.
 */
function VideoComing({ label }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <PlayCircle
        size={26}
        strokeWidth={1.2}
        style={{ color: "var(--cr-ruby)", opacity: 0.6 }}
        className="shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p
          className="font-display font-light"
          style={{ color: "var(--cr-ink)", fontSize: "0.98rem", lineHeight: 1.4 }}
        >
          {label}
        </p>
        <p
          className="font-display italic font-light"
          style={{ color: "var(--cr-ink-soft)", fontSize: "0.82rem", opacity: 0.75 }}
        >
          Vidéo en préparation
        </p>
      </div>
    </div>
  );
}

/**
 * Callout — encart éditorial : icône dans un cercle discret + libellé
 * en petites capitales rubis + valeur en Playfair. Aucun cadre ni
 * bordure : la structure repose entièrement sur la typographie et
 * l'espace, pour une lecture qui coule.
 */
function Callout({ icon, label, value, mono, cta, whatsappCta, image, imageAlt }) {
  const Icon = ICONS[icon] || Sparkles;
  const { contact, mediaUrl } = useContent();
  const waHref = whatsappCta && contact?.whatsappNumber
    ? `https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(whatsappCta.message || "")}`
    : null;
  const imgUrl = image ? (mediaUrl ? mediaUrl(image) : image) : null;

  return (
    <div className="flex items-start gap-4">
      <span
        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1"
        style={{
          background: "rgba(232, 184, 189, 0.16)",
          border: "1px solid rgba(139, 37, 48, 0.20)",
        }}
      >
        <Icon size={14} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
          <p
            className="font-body text-[10px] tracking-[0.24em] uppercase"
            style={{ color: "var(--cr-ruby)" }}
          >
            {label}
          </p>
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="callout-whatsapp"
              className="inline-flex items-center gap-1.5 font-body text-[10px] tracking-[0.18em] uppercase transition-colors"
              style={{
                color: "#128C7E",
                borderBottom: "1px dotted rgba(18,140,126,0.5)",
                paddingBottom: "1px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#0F7A6E")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#128C7E")}
            >
              <MessageCircle size={11} strokeWidth={1.8} />
              {whatsappCta.label}
            </a>
          )}
        </div>
        <p
          className={`${mono ? "font-mono" : "font-display font-light"} whitespace-pre-line`}
          style={{
            color: "var(--cr-ink)",
            fontSize: mono ? "0.95rem" : "1.02rem",
            lineHeight: 1.55,
            letterSpacing: mono ? "0.02em" : "normal",
          }}
        >
          {mono ? value : renderRich(value)}
        </p>
        {imgUrl && (
          <div
            data-testid="callout-image"
            className="mt-3 overflow-hidden"
            style={{
              borderRadius: "10px",
              border: "1px solid rgba(184, 153, 104, 0.35)",
              maxWidth: "22rem",
            }}
          >
            <img
              src={imgUrl}
              alt={imageAlt || label || ""}
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
        )}
        {cta && (
          <div className="mt-3">
            {cta.href && cta.href.startsWith("/") ? (
              <Link
                to={cta.href}
                data-testid="callout-cta"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-[10px] tracking-[0.2em] uppercase transition-all"
                style={{
                  border: "1px solid rgba(139, 37, 48, 0.35)",
                  color: "var(--cr-ruby)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--cr-ruby)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--cr-ruby)";
                }}
              >
                {cta.label}
                <ArrowUpRight size={12} strokeWidth={1.5} />
              </Link>
            ) : (
              <a
                href={cta.href}
                target={cta.href?.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid="callout-cta"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-[10px] tracking-[0.2em] uppercase transition-all"
                style={{
                  border: "1px solid rgba(139, 37, 48, 0.35)",
                  color: "var(--cr-ruby)",
                }}
              >
                {cta.label}
                <ArrowUpRight size={12} strokeWidth={1.5} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CompareTwo — comparaison éditoriale : 2 colonnes sans cadre, séparées
 * par un simple filet doré (barre verticale sur desktop, horizontale
 * sur mobile). Un CTA discret sous les 2 colonnes.
 */
function CompareTwo({ heading, left, right, cta }) {
  const [, setSearchParams] = useSearchParams();
  const smoothScrollTo = (anchor) => (e) => {
    e.preventDefault();
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const activateMode = (col) => (e) => {
    e.preventDefault();
    if (col.mode) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(col.modeParam || "mode", col.mode);
        return next;
      }, { replace: true });
    }
    if (col.anchor) {
      setTimeout(() => {
        const el = document.getElementById(col.anchor);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 30);
    }
  };
  const renderColumn = (col, i) => {
    const Icon = ICONS[col.icon] || Sparkles;
    const inner = (
      <>
        <span
          className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1"
          style={{
            background: "rgba(232, 184, 189, 0.16)",
            border: "1px solid rgba(139, 37, 48, 0.22)",
          }}
        >
          <Icon size={14} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
        </span>
        <div className="flex-1">
          <p
            className="font-display font-light leading-tight"
            style={{ color: "var(--cr-ink)", fontSize: "1.02rem" }}
          >
            {col.title}
          </p>
          {col.text && (
            <p
              className="font-display font-light mt-1"
              style={{ color: "var(--cr-ink-soft)", fontSize: "0.9rem", lineHeight: 1.55 }}
            >
              {col.text}
            </p>
          )}
        </div>
        {col.anchor && (
          <ArrowUpRight
            size={13}
            strokeWidth={1.5}
            className="shrink-0 mt-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            style={{ color: "var(--cr-ruby)", opacity: 0.55 }}
          />
        )}
      </>
    );
    if (col.anchor) {
      return (
        <a
          key={i}
          href={`#${col.anchor}`}
          onClick={col.mode ? activateMode(col) : smoothScrollTo(col.anchor)}
          data-testid={`compare-col-${col.anchor}`}
          className="group flex items-start gap-3 p-2 -m-2 rounded-lg transition-colors"
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(232,184,189,0.10)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {inner}
        </a>
      );
    }
    return (
      <div key={i} className="flex items-start gap-3">
        {inner}
      </div>
    );
  };

  return (
    <section>
      {heading && (
        <div className="flex items-baseline gap-3 mb-5">
          <h3
            className="font-display font-light italic"
            style={{ color: "var(--cr-ink)", fontSize: "1.15rem" }}
          >
            {heading}
          </h3>
          <span
            className="flex-1 h-[1px]"
            style={{ background: "rgba(184, 153, 104, 0.30)" }}
          />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 relative">
        <span
          aria-hidden="true"
          className="hidden sm:block absolute left-1/2 top-2 bottom-2 w-px -translate-x-1/2"
          style={{ background: "rgba(184, 153, 104, 0.35)" }}
        />
        {renderColumn(left, 0)}
        {renderColumn(right, 1)}
      </div>
      {cta && (
        <div className="mt-6 flex justify-center">
          {cta.href.startsWith("/") ? (
            <Link
              to={cta.href}
              data-testid="compare-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all"
              style={{ background: "var(--cr-ruby)", color: "#FAF4EC" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--cr-ruby-deep)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--cr-ruby)")}
            >
              {cta.label}
              <ArrowUpRight size={12} strokeWidth={1.5} />
            </Link>
          ) : (
            <a
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="compare-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all"
              style={{ background: "var(--cr-ruby)", color: "#FAF4EC" }}
            >
              {cta.label}
              <ArrowUpRight size={12} strokeWidth={1.5} />
            </a>
          )}
        </div>
      )}
    </section>
  );
}

/** KV — liste clé/valeur (par ex. procédures) */
function KV({ heading, pairs }) {
  return (
    <section>
      {heading && <BlockHeading>{heading}</BlockHeading>}
      <dl className="space-y-2.5">
        {pairs.map(([k, v], i) => (
          <div
            key={i}
            className="flex gap-4 py-2"
            style={{ borderBottom: "1px solid rgba(184, 153, 104, 0.22)" }}
          >
            <dt
              className="shrink-0 font-body text-[10px] tracking-[0.22em] uppercase pt-1"
              style={{ color: "var(--cr-ruby)", minWidth: "5.5rem" }}
            >
              {k}
            </dt>
            <dd
              className="font-display font-light flex-1"
              style={{ color: "var(--cr-ink)", fontSize: "0.98rem", lineHeight: 1.5 }}
            >
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** List — liste à puces élégantes (petites marques dorées) */
function List({ heading, items }) {
  return (
    <section>
      {heading && <BlockHeading>{heading}</BlockHeading>}
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex gap-3 items-start font-display font-light py-1"
            style={{ color: "var(--cr-ink)", fontSize: "0.98rem", lineHeight: 1.5 }}
          >
            <span
              className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--cr-gold)" }}
            />
            {it}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Steps — liste numérotée pour les procédures */
function Steps({ heading, intro, items }) {
  return (
    <section>
      {heading && <BlockHeading>{heading}</BlockHeading>}
      {intro && (
        <p
          className="font-display font-light mb-5"
          style={{ color: "var(--cr-ink-soft)", fontSize: "0.96rem", lineHeight: 1.55 }}
        >
          {intro}
        </p>
      )}
      <ol className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex gap-4 items-start">
            <span
              className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-display text-[13px]"
              style={{
                background: "rgba(232, 184, 189, 0.16)",
                border: "1px solid rgba(139, 37, 48, 0.28)",
                color: "var(--cr-ruby)",
              }}
            >
              {i + 1}
            </span>
            <p
              className="font-display font-light flex-1 pt-1"
              style={{ color: "var(--cr-ink)", fontSize: "0.98rem", lineHeight: 1.5 }}
            >
              {renderRich(it)}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Warning — consignes strictes, présentation aérée sans cadre lourd */
function Warning({ heading, items }) {
  return (
    <div className="py-2">
      <div className="flex items-center gap-2.5 mb-4">
        <AlertTriangle size={14} strokeWidth={1.6} style={{ color: "var(--cr-ruby)" }} />
        <p
          className="font-body text-[10px] tracking-[0.28em] uppercase"
          style={{ color: "var(--cr-ruby)" }}
        >
          {heading}
        </p>
        <span
          className="flex-1 h-[1px]"
          style={{ background: "rgba(139, 37, 48, 0.20)" }}
        />
      </div>
      <ul className="space-y-2 pl-1">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex gap-2.5 items-start font-display font-light"
            style={{ color: "var(--cr-ink)", fontSize: "0.95rem", lineHeight: 1.5 }}
          >
            <Check
              size={13}
              strokeWidth={2}
              className="mt-1.5 shrink-0"
              style={{ color: "var(--cr-ruby)" }}
            />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Note — paragraphe discret en italique */
function Note({ text }) {
  return (
    <p
      className="font-display italic font-light border-l-2 pl-4 py-1"
      style={{
        color: "var(--cr-ink-soft)",
        fontSize: "0.95rem",
        lineHeight: 1.55,
        borderColor: "var(--cr-gold)",
      }}
    >
      {text}
    </p>
  );
}

/** TelLink — encart appelable (tap-to-call). value = numéro affiché, href = tel:...  */
function TelLink({ label, value, href, secondary }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <a
        href={href}
        data-testid="guide-tel-link"
        className="group flex items-center gap-3 p-4 transition-all"
        style={{
          borderRadius: "8px",
          border: "1px solid rgba(139, 37, 48, 0.28)",
          background: "rgba(232, 184, 189, 0.10)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(232, 184, 189, 0.22)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(232, 184, 189, 0.10)")}
      >
        <span
          className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "var(--cr-ruby)" }}
        >
          <Phone size={15} strokeWidth={1.6} style={{ color: "#FAF4EC" }} />
        </span>
        <div className="flex-1 min-w-0">
          <p
            className="font-body text-[10px] tracking-[0.24em] uppercase"
            style={{ color: "var(--cr-ruby)" }}
          >
            {label}
          </p>
          <p
            className="font-mono"
            style={{ color: "var(--cr-ink)", fontSize: "0.98rem", letterSpacing: "0.02em" }}
          >
            {value}
          </p>
        </div>
      </a>
      {secondary && (
        <a
          href={secondary.href}
          data-testid="guide-sms-link"
          className="group flex items-center gap-3 p-4 transition-all"
          style={{
            borderRadius: "8px",
            border: "1px solid rgba(139, 37, 48, 0.28)",
            background: "rgba(255,253,249,0.6)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(232, 184, 189, 0.14)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,253,249,0.6)")}
        >
          <span
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              border: "1px solid rgba(139, 37, 48, 0.35)",
              background: "transparent",
            }}
          >
            <MessageCircle size={15} strokeWidth={1.5} style={{ color: "var(--cr-ruby)" }} />
          </span>
          <div className="flex-1 min-w-0">
            <p
              className="font-body text-[10px] tracking-[0.24em] uppercase"
              style={{ color: "var(--cr-ruby)" }}
            >
              {secondary.label}
            </p>
            <p
              className="font-display font-light"
              style={{ color: "var(--cr-ink)", fontSize: "0.95rem" }}
            >
              {secondary.value}
            </p>
          </div>
        </a>
      )}
    </div>
  );
}

/** MailLink — encart email cliquable (mailto:) */
function MailLink({ label, value, href }) {
  return (
    <a
      href={href}
      data-testid="guide-mail-link"
      className="flex items-center gap-4 p-4 md:p-5 transition-all"
      style={{
        borderRadius: "8px",
        border: "1px solid rgba(184, 153, 104, 0.35)",
        background: "linear-gradient(180deg, rgba(255,253,249,0.85) 0%, rgba(255,253,249,0.4) 100%)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(232, 184, 189, 0.10)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "linear-gradient(180deg, rgba(255,253,249,0.85) 0%, rgba(255,253,249,0.4) 100%)")}
    >
      <span
        className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center"
        style={{ border: "1px solid rgba(139, 37, 48, 0.20)", background: "rgba(232, 184, 189, 0.14)" }}
      >
        <Mail size={15} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[10px] tracking-[0.24em] uppercase mb-1" style={{ color: "var(--cr-ruby)" }}>
          {label}
        </p>
        <p className="font-mono truncate" style={{ color: "var(--cr-ink)", fontSize: "0.95rem" }}>
          {value}
        </p>
      </div>
      <ArrowUpRight size={14} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
    </a>
  );
}

/** InstaLink — encart Instagram cliquable */
function InstaLink({ label, value, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="guide-insta-link"
      className="flex items-center gap-4 p-4 md:p-5 transition-all"
      style={{
        borderRadius: "8px",
        border: "1px solid rgba(184, 153, 104, 0.35)",
        background: "linear-gradient(180deg, rgba(255,253,249,0.85) 0%, rgba(255,253,249,0.4) 100%)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(232, 184, 189, 0.10)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "linear-gradient(180deg, rgba(255,253,249,0.85) 0%, rgba(255,253,249,0.4) 100%)")}
    >
      <span
        className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center"
        style={{ border: "1px solid rgba(139, 37, 48, 0.20)", background: "rgba(232, 184, 189, 0.14)" }}
      >
        <Instagram size={15} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[10px] tracking-[0.24em] uppercase mb-1" style={{ color: "var(--cr-ruby)" }}>
          {label}
        </p>
        <p className="font-mono truncate" style={{ color: "var(--cr-ink)", fontSize: "0.95rem" }}>
          {value}
        </p>
      </div>
      <ArrowUpRight size={14} strokeWidth={1.4} style={{ color: "var(--cr-ruby)" }} />
    </a>
  );
}

/** ExternalLink — bouton lien externe raffiné */
function ExternalLink({ label, href }) {
  return (
    <div className="flex justify-center pt-1">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all duration-500"
        style={{
          border: "1px solid rgba(139, 37, 48, 0.35)",
          color: "var(--cr-ruby)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--cr-ruby)";
          e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = "var(--cr-ruby)";
        }}
      >
        {label}
        <ArrowUpRight size={12} strokeWidth={1.5} />
      </a>
    </div>
  );
}

/** Petit titre de section à l'intérieur d'un chapitre */
function BlockHeading({ children }) {
  return (
    <div className="flex items-baseline gap-3 mb-3">
      <h3
        className="font-display font-light"
        style={{ color: "var(--cr-ink)", fontSize: "1.15rem" }}
      >
        {children}
      </h3>
      <span
        className="flex-1 h-[1px]"
        style={{ background: "rgba(184, 153, 104, 0.35)" }}
      />
    </div>
  );
}

/** Fallback pour un slug inconnu */
function UnknownChapter({ navigate, uiT }) {
  return (
    <div
      data-testid="guide-chapter-unknown"
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #FBF5EA 0%, #FDF9F0 40%, #FFFFFF 100%)",
      }}
    >
      <div className="w-full max-w-2xl mx-auto px-5 sm:px-8 pt-6 md:pt-8">
        <button
          type="button"
          onClick={() => navigate("/guide")}
          className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.2em] uppercase"
          style={{ color: "var(--cr-ink-soft)" }}
        >
          <ArrowLeft size={14} strokeWidth={1.4} />
          {uiT("backToGuide")}
        </button>
      </div>
      <main className="cr-guide-open w-full max-w-lg mx-auto text-center px-5 sm:px-8 pt-16 md:pt-24 pb-24">
        <Sparkles
          size={22}
          strokeWidth={1.2}
          style={{ color: "var(--cr-ruby)" }}
          className="mx-auto mb-4"
        />
        <p
          className="font-display italic font-light mb-2"
          style={{ color: "var(--cr-ink)", fontSize: "1.15rem", lineHeight: 1.55 }}
        >
          {uiT("chapterUnknown1")}
        </p>
        <p
          className="font-body text-[13px] leading-relaxed"
          style={{ color: "var(--cr-ink-soft)" }}
        >
          {uiT("chapterUnknown2")}
        </p>
        <Link
          to="/guide"
          className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full font-body text-[11px] tracking-[0.22em] uppercase"
          style={{ background: "var(--cr-ruby)", color: "#FAF4EC" }}
        >
          <BookOpen size={14} strokeWidth={1.4} />
          {uiT("backToSummary")}
        </Link>
      </main>
    </div>
  );
}

/**
 * Gallery — mini-galerie éditoriale de 1 à 6 photos.
 * Layout adaptatif : 1 → pleine largeur / 2 → côte à côte / 3-6 → grille responsive.
 * Caption optionnelle sous chaque photo (petit italique doré).
 */
function Gallery({ eyebrow, heading, photos = [] }) {
  const { mediaUrl } = useContent();
  if (!photos.length) return null;
  const cols = photos.length === 1 ? "grid-cols-1"
    : photos.length === 2 ? "grid-cols-2"
    : "grid-cols-2 sm:grid-cols-3";
  return (
    <div data-testid="block-gallery">
      {(eyebrow || heading) && (
        <div className="text-center mb-5">
          {eyebrow && (
            <p
              className="font-body text-[10px] tracking-[0.28em] uppercase mb-1.5"
              style={{ color: "var(--cr-ruby)" }}
            >
              {eyebrow}
            </p>
          )}
          {heading && (
            <p
              className="font-display font-light"
              style={{ color: "var(--cr-ink)", fontSize: "clamp(1.2rem, 2.4vw, 1.5rem)" }}
            >
              {heading}
            </p>
          )}
        </div>
      )}
      <div className={`grid ${cols} gap-3 md:gap-4`}>
        {photos.map((p, i) => {
          const src = typeof p === "string" ? p : p.src;
          const alt = typeof p === "string" ? "" : p.alt || "";
          const caption = typeof p === "string" ? null : p.caption;
          const url = src ? (mediaUrl ? mediaUrl(src) : src) : null;
          if (!url) return null;
          return (
            <figure key={i} className="text-center">
              <div
                className="w-full overflow-hidden"
                style={{
                  borderRadius: "10px",
                  border: "1px solid rgba(184, 153, 104, 0.35)",
                  aspectRatio: photos.length === 1 ? "16 / 9" : "1 / 1",
                }}
              >
                <img
                  src={url}
                  alt={alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.04]"
                />
              </div>
              {caption && (
                <figcaption
                  className="font-display italic font-light mt-2"
                  style={{ color: "var(--cr-ink-soft)", fontSize: "0.85rem" }}
                >
                  {caption}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>
    </div>
  );
}

